/**
 * Bestand (#785): alle Geräte des Unternehmens, ohne Umweg über Liegenschaft, Etage oder Raum.
 *
 * Jede andere Geräteliste im Portal ist an einen Ort gebunden — die Projektverwaltung an die
 * Adresse einer Liegenschaft, Etage und Raum an ihren Plan. Ein Gerät, das in der App ohne
 * Etagenplan registriert wurde (#720), hat keinen dieser Orte und war deshalb nirgends zu
 * sehen. Dieses Panel zeigt den gesamten Bestand und findet auf Wunsch genau diese Geräte;
 * den Standort selbst vergibt die App am Gerät.
 */

import { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import AssetList from "../components/AssetList";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/** `?unassigned=1` — so a floor page can link straight to the devices no plan shows. */
export const UNASSIGNED_PARAM = "unassigned";

/** The page parameter AssetList keeps in the URL; a changed filter starts on page one. */
const ASSET_LIST_PAGE_PARAM = "assetListPage";

export default function AssetInventoryPanel() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const switchId = useId();

  const unassignedOnly = searchParams.get(UNASSIGNED_PARAM) === "1";

  const setUnassignedOnly = useCallback(
    (next: boolean) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);

          if (next) {
            params.set(UNASSIGNED_PARAM, "1");
          } else {
            params.delete(UNASSIGNED_PARAM);
          }
          params.delete(ASSET_LIST_PAGE_PARAM);

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        {t("assetInventoryPanel.description")}
      </p>
      <div className="flex items-center gap-2">
        <Switch
          data-guide-id="settings.assetInventory.unassigned"
          id={switchId}
          checked={unassignedOnly}
          onCheckedChange={setUnassignedOnly}
        />
        <Label htmlFor={switchId}>{t("assetInventoryPanel.unassignedOnly")}</Label>
      </div>
      <AssetList
        showLocation
        unassignedOnly={unassignedOnly}
        emptyMessage={unassignedOnly ? t("assetInventoryPanel.unassignedEmpty") : undefined}
      />
    </div>
  );
}
